import { useCurrency, walletSettings } from '@bz/common';
import { keyArray } from '@solid-primitives/keyed';
import { getCoinIcon, isFiat } from '@bz/wallet/src/utils';
import Decimal from 'decimal.js-light';
import { JSX } from 'solid-js';

import { BetSlipType, OddsSettings, BetStatus, BetslipStatus, SingleSubType } from '../../types';
import { PlaceParams, BetPaymentMethod, BetsOdds } from '../../types/betslip';
import { useBetSlipStore, getComboOrSystemBets } from '../../useBetSlip';
import { ActionLocked, LockedKey, useLock } from './ActionLocked';
import { calcPotentials, getBetCount } from '../../betting-systems';
import { handleBetError, type BetErrorResult } from '../../handleBetError';
import { Overlay } from '../overlay/Overlay';
import { BetStakeInputTip } from './betItemCom/BetStakeInputTip';
import { useTicketsData } from '../openBets/hook';
import { Tooltip } from './Tooltip';
import { useBonusConfig } from '../shared/hooks';
import VipCreditNotice from '../shared/components/VipCreditNotice';
import { CheckCreditBetLimit } from '../shared/components/CheckCreditBetLimitPop';

import currencyConvertHint from '~/utils/formatCurrency';
import { createLatestOdds } from '~/primitives/createOddsWs';
import useNegativeEquity from '~/hook/useNegativeEquity';
import { OutcomeStatus } from '~/constants';
import { useResponsibleBetDepositLimit } from '~/hook/useBetCredit';

/**
 * 投注操作区域核心组件
 * 包含金额输入、快速投注按钮、统计信息和投注按钮
 */
export const BetActionArea = () => {
  const { getOpenBetCount } = useTicketsData();
  const [store, dispatch] = useBetSlipStore();
  const {
    latestCurrency,
    source,
    isCash,
    isBonus,
    isBetCreditOn,
    setIsBetCreditOn,
    addVipBalance,
    currencyCashBalanceDecimal,
    currencyAvailableCreditBalance,
  } = useCurrency();
  const { isNegative, getNegativeDepositUrl } = useNegativeEquity();

  const getComboType = createMemo(() => store.comboType);
  const { minBetOdds, minBet: bonusMinBet, maxCreditBet } = useBonusConfig();
  const { canPlaceBet, canDeposit } = useResponsibleBetDepositLimit();

  const currentCurrencyInfo = createMemo(() => currencyConfig[latestCurrency()]);
  // 获取最小值（法币/加密货币/现金券）
  const finalMinBet = createMemo(() => (isBonus() ? bonusMinBet() : currentCurrencyInfo()?.minBet) || 0);

  const [showOverlay, setShowOverlay] = createSignal(false);

  // 统一订阅一下，方便实时下单的时候，赔率实时更新 后期计划全部统一迁移到hook,不要子组件单独订阅
  const latestOdds = createMemo(
    keyArray(
      () => store.odds,
      (odd) => (odd.uniqueKey ? odd.uniqueKey : odd.code),
      (odd) => createLatestOdds(odd)
    )
  );

  const isShowOddsLimit = createMemo(() => {
    const ret = latestOdds().some((o) => {
      if (o.type === SingleSubType.CustomBet) {
        return o.selections?.some((s) => s.odds < minBetOdds());
      }
      return o.odds < minBetOdds();
    });
    return ret;
  });

  const isBonusOddsCanNotBet = createMemo(() => {
    // 现金券
    const isSingle = store.betType === BetSlipType.Single;
    if (isSingle) {
      // single只有一个赔率满足大于最小赔率，就能下注
      const canBet = latestOdds().some((o) => {
        if (o.type === SingleSubType.CustomBet) {
          return o.selections?.some((s) => s.odds > minBetOdds());
        }
        return o.odds > minBetOdds();
      });
      return !canBet;
    }
    // 只有一个赔率不满足，就不能下注
    const canNotBet = latestOdds().some((o) => {
      if (o.type === SingleSubType.CustomBet) {
        return o.selections?.some((s) => s.odds < minBetOdds());
      }
      return o.odds < minBetOdds();
    });
    return canNotBet;
  });

  const [countdownSeconds, setCountdownSeconds] = createSignal(1);
  const [status, setStatus] = createSignal<BetslipStatus>(BetslipStatus.LOADING);
  const [message, setMessage] = createSignal<string | JSX.Element>('');
  const [isSelectOpen, setIsSelectOpen] = createSignal(false);
  const [localErrCode, setLocalErrCode] = createSignal<string | undefined>(undefined);
  let timerId: number | null = null;
  let timeOutId: number | null = null;
  const startCountdown = () => {
    if (timerId) clearInterval(timerId);
    setStatus(BetslipStatus.LOADING);
    setCountdownSeconds(5);
    setMessage('');

    // 确保之前的定时器被清理
    stopCountdown();
    timerId = window.setInterval(() => {
      setCountdownSeconds((prev) => {
        if (prev <= 1) {
          setMessage(t('Your bet is taking longer than usual. Please wait patiently.'));
          // 开始第二个计时。。。
          secondStageTimer();
          stopCountdown();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // 渲染错误信息
  // const renderErrorMessage = (title: string, subTitle: string) => {
  //   return (
  //     <div class="flex flex-col items-center">
  //       <span class="text-neutral-main1 text-sm font-semibold">{title}</span>
  //       <span class="text-neutral-main2">{subTitle}</span>
  //     </div>
  //   );
  // };

  // time out timer
  const secondStageTimer = () => {
    if (timeOutId) clearTimeout(timeOutId);
    timeOutId = window.setTimeout(() => {
      setMessage(
        <div class="flex flex-col items-center">
          <span class="text-neutral-main1 text-sm font-semibold">{t('Your bet is still being confirmed.')}</span>
          <span class="text-neutral-main2">{t('We’ll notify you as soon as we have an update.')}</span>
        </div>
      );
      setStatus(BetslipStatus.TIMEOUT);
    }, 15000);
  };

  // 手动停止
  const stopCountdown = () => {
    if (timerId !== null) {
      clearInterval(timerId);
      timerId = null;
    }
  };

  const stopCount = () => {
    if (timeOutId) {
      clearInterval(timeOutId);
      timeOutId = null;
    }
  };

  onCleanup(() => {
    stopCountdown();
    stopCount();
  });

  const _createOrder = useAction(post.createOrderUserPlace);

  const totStake = (type: BetSlipType) => {
    const covertAmount = (n: Decimal) => currency.convertCurrency(new Decimal(n), latestCurrency(), latestCurrency());
    let totalStake = new Decimal(0);
    switch (type) {
      case BetSlipType.Single: {
        totalStake = finalLatestOdds().reduce((acc: Decimal, o) => {
          const amount = new Decimal(o.amount || 0);

          const isBasicValid = o.active === 1 && amount.gte(finalMinBet());

          if (o.type === SingleSubType.CustomBet) {
            const selections = o.selections || [];
            const isCustomValid = selections.length >= 2 && selections.every((s) => s.active === 1);
            return isBasicValid && isCustomValid ? acc.add(amount) : acc;
          }

          return isBasicValid ? acc.add(amount) : acc;
        }, new Decimal(0));
        totalStake = covertAmount(totalStake);
        break;
      }
      case BetSlipType.Combo:
        totalStake = covertAmount(new Decimal(store.comboAmount));
        break;
      case BetSlipType.System:
        totalStake = new Decimal(store.comboAmount.toString()).mul(getBetCount(getComboType()));
        break;
    }
    return totalStake.toNumber();
  };

  // 计算总赔率 只能计算combo的赔率
  const totalOdds = createMemo(() => {
    const total = new Decimal(1);
    return getComboOrSystemBets(latestOdds()).reduce((acc, odd) => acc.mul(odd.odds), total);
  });

  // 数据转换
  const transformBets = (latestOdds: { [K in keyof BetsOdds]: BetsOdds[K] }[], betType: BetSlipType) => {
    const isSingle = betType === BetSlipType.Single;
    const isCombo = betType === BetSlipType.Combo;
    const isSystem = betType === BetSlipType.System;

    const normalToSelection = <T extends { matchId: string; code: string; odds: number }>(o: T) => ({
      matchId: o.matchId,
      code: o.code,
      odds: o.odds,
    });

    if (isSingle) {
      return latestOdds.map((o) => {
        const isCustom = o.type === SingleSubType.CustomBet;
        const selections = isCustom && o.selections ? o.selections.map(normalToSelection) : [normalToSelection(o)];
        return {
          itemType: o.type,
          comboType: null,
          copyNo: o.copyNo,
          copyType: o.copyType,
          odds: o.odds,
          stake: o.amount,
          selections,
        };
      });
    }

    // ---- 合并所有 combo 和 system 的 selection -----
    const allSelections = getComboOrSystemBets(latestOdds).map((o) => normalToSelection(o));

    const odds = isCombo ? totalOdds().toDecimalPlaces(2, Decimal.ROUND_DOWN).toFixed(2) : 2; //system 没有总赔率 因为api要 所以给个2
    return [
      {
        itemType: isCombo ? BetSlipType.Combo : BetSlipType.System,
        comboType: isSystem ? getComboType() : null,
        copyNo: latestOdds[0]?.copyNo,
        copyType: latestOdds[0]?.copyType,
        odds,
        stake: totStake(store.betType),
        selections: allSelections,
      },
    ];
  };

  const finalLatestOdds = createMemo(() => {
    if (!isCash()) {
      return latestOdds().filter((item) => {
        return item.odds > minBetOdds();
      });
    }
    return latestOdds();
  });

  // 有效的参数
  const validOdds = createMemo(() => {
    if (store.betType === BetSlipType.Single) {
      return finalLatestOdds().filter((o) => {
        // 基础条件：激活状态且金额 > 0
        const isBasicValid = o.active === 1 && new Decimal(o.amount || 0).gte(finalMinBet());

        // 定制投注额外校验：至少2项且全激活
        if (o.type === SingleSubType.CustomBet) {
          const selections = o.selections || [];
          const isCustomValid = selections.length >= 2 && selections.every((s) => s.active === 1);
          return isBasicValid && isCustomValid;
        }

        return isBasicValid;
      });
    }
    return finalLatestOdds();
  });

  type Expand<T> = T extends object ? { [K in keyof T]: T[K] } : T;
  type OriginalParams = Parameters<typeof _createOrder>[0];

  // 订单参数
  const currentOrderParams = createMemo<Expand<PlaceParams>>(() => {
    return {
      betType: store.betType,
      currency: latestCurrency(),
      paymentMethod: source() as BetPaymentMethod,
      useCredit: isBetCreditOn() && isCash() ? 1 : 0,
      totalStake: totStake(store.betType),
      acceptOddsChange: store.oddsSettings,
      bets: transformBets(validOdds(), store.betType),
      timeOut: 20e3, // 最大20s秒等待时间
    };
  });

  // 关闭错误弹窗
  let overlayRef: HTMLDivElement | undefined;
  createEffect(() => {
    if (!showOverlay()) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (!overlayRef) return;

      const isClickInside = overlayRef.contains(e.target as Node);
      if (!isClickInside) {
        if (status() !== BetslipStatus.LOADING) {
          setShowOverlay(false);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    onCleanup(() => {
      document.removeEventListener('click', handleClickOutside);
    });
  });

  const createOrder = async () => {
    const { lock } = useLock();

    // 锁定操作区域
    const unlock = lock([
      LockedKey.bet_items,
      store.betType === BetSlipType.Single ? LockedKey.bet_type_parlay : LockedKey.bet_type_single,
    ]);
    try {
      const params = currentOrderParams();
      // 当前下注的参数code集合
      const betOdds = validOdds();

      setShowOverlay(true);
      startCountdown();
      dispatch({ type: 'CHANGE_BET_STATUS', status: BetStatus.LOADING });

      await _createOrder(params as OriginalParams)
        .then((res) => {
          console.log('订单创建成功', res);
          setShowOverlay(false);
          stopCountdown();
          dispatch({ type: 'CHANGE_BET_STATUS', status: BetStatus.SUCCESS });
          revalidate([get.getUserInsuranceGetInsuranceStatus.key]);
          // 当 当前投注类型是combo/system时
          if (store.betType !== BetSlipType.Single) {
            if (store.odds.some((o) => o.type === SingleSubType.CustomBet)) {
              const codeList = store.odds.filter((o) => o.type !== SingleSubType.CustomBet).map((s) => s.code);
              codeList.forEach((code) => {
                dispatch({
                  type: 'REMOVE_ODD',
                  uniqueKey: code,
                });
              });
              return;
            }
          }

          // 留下不满足条件的
          if (store.betType === BetSlipType.Single) {
            betOdds.forEach((o) => {
              dispatch({
                type: 'REMOVE_ODD',
                uniqueKey: o.uniqueKey,
              });
            });
          } else {
            dispatch({ type: 'CLEAR_ALL_BETS' });
          }
        })
        .catch((error) => {
          stopCountdown();
          stopCount();
          console.error('订单创建失败', error.raw);
          const {
            status: getStatus,
            localErrCode: errorCode,
            message,
            failedSelections,
          }: BetErrorResult = handleBetError(error);

          // 前端请求20s后 结果未返回时
          if (store.betStatus === BetStatus.LOADING && status() === BetslipStatus.TIMEOUT) {
            if (store.betType === BetSlipType.Single) {
              betOdds.forEach((o) => {
                dispatch({
                  type: 'REMOVE_ODD',
                  uniqueKey: o.uniqueKey,
                });
              });
            } else {
              dispatch({ type: 'CLEAR_ALL_BETS' });
            }
            dispatch({ type: 'CHANGE_BET_STATUS', status: BetStatus.NORMAL });
            return;
          }

          // 将失败的留在betslip
          const codeSet = new Set(failedSelections?.map((c) => c.oddsCode));
          if (codeSet.size) {
            store.odds.forEach((item) => {
              if (!codeSet.has(item.code)) {
                dispatch({
                  type: 'REMOVE_ODD',
                  uniqueKey: item.code,
                });
              }
            });
          }
          setLocalErrCode(errorCode);
          setStatus(getStatus || BetslipStatus.ERROR);
          setMessage(message);
          dispatch({ type: 'CHANGE_BET_STATUS', status: BetStatus.NORMAL });
        });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      toast(message);
    } finally {
      console.log('bet catch finally 👀');
      stopCountdown();
      stopCount();
      unlock();
    }
  };

  /**
   * 统计信息块组件
   * 用于显示总投注额、总赔率、潜在收益等信息
   */
  const StatBlock = (props: ParentProps) => {
    return (
      <div class="h-7 mb-2 relative text-neutral-main2 select-none justify-between text-sm font-semibold flex items-center">
        {props.children}
      </div>
    );
  };

  // 计算总投注额
  const totalBet = createMemo(() => {
    let total: Decimal = new Decimal(0);
    switch (store.betType) {
      case BetSlipType.Single:
        total = latestOdds().reduce((acc, odd) => {
          if (odd.active !== OutcomeStatus.Active) {
            return acc;
          }
          return acc.add(new Decimal(odd.amount || 0));
        }, total);
        break;
      case BetSlipType.Combo:
        total = new Decimal(store.comboAmount);
        break;
      case BetSlipType.System:
        total = new Decimal(store.comboAmount * getBetCount(getComboType()));
        break;
    }
    return total;
  });

  /**
   * 计算潜在收益 例如三注赔率分别是A B C，分别投注10 20 30元
   * single: 每注的金额 * 赔率，然后每注相加
   * combo:  每注赔率相乘，然后再乘投注金额
   * system: 和single一样，如2/3， 必须为三场不一样的比赛，每一个组合的赔率=各个selection赔率相乘，然后每注相加
   */
  const potentialWin = createMemo(() => {
    let total: Decimal = new Decimal(0);
    const initVal = new Decimal(0);

    switch (store.betType) {
      case BetSlipType.Single: {
        return latestOdds().reduce((acc, item) => {
          if (item.active !== OutcomeStatus.Active) {
            return acc;
          }
          // 计算已激活项的奖金
          const oddsVal = new Decimal(item.odds || 0);
          const amountVal = new Decimal(item.amount || 0);

          const payout = oddsVal.mul(amountVal);
          return acc.plus(payout);
        }, initVal);
      }
      case BetSlipType.Combo:
        total = totalBet().mul(totalOdds().toDecimalPlaces(2, Decimal.ROUND_DOWN).toFixed(2));
        break;
      case BetSlipType.System:
        total = calcPotentials(latestOdds(), getComboType(), store.comboAmount);
        break;
    }

    return total;
  });

  const finalTotalWin = createMemo(() => {
    return isCash() ? potentialWin() : potentialWin().sub(totalBet());
  });

  // 是否有关闭的盘口
  const hasClosedOdds = createMemo(() => {
    if (latestOdds().length === 0) return true;

    const isUnavailable = (item: { marketStatus?: number; active: number }): boolean => {
      return item.marketStatus !== 1 || item.active !== 1;
    };

    // 1. 非单关 (Combo/System)
    if (store.betType !== BetSlipType.Single) {
      return latestOdds()
        .filter((odd) => odd.type !== SingleSubType.CustomBet)
        .some(isUnavailable);
    }

    // 2. 单关模式 (Single)
    const isAnyItemAvailable = latestOdds().some((odd) => {
      if (odd.type === SingleSubType.CustomBet) {
        const hasSelections = odd.selections && odd.selections.length >= 2;
        const allSelectionsActive = odd.selections?.every((item) => !isUnavailable(item));
        return hasSelections && allSelectionsActive;
      }

      return !isUnavailable(odd);
    });

    return !isAnyItemAvailable;
  });

  // 检查是否可以投注
  const canBet = createMemo(() => {
    if (isBonusOddsCanNotBet() && isBonus()) {
      return false;
    }

    if (store.betType === BetSlipType.Single) {
      return latestOdds().some((odd) => {
        const isBasicValid = new Decimal(odd.amount || 0).gte(finalMinBet()) && odd.active === 1;
        if (!isBasicValid) return false;

        if (odd.type === SingleSubType.CustomBet) {
          return (odd.selections?.length ?? 0) >= 2;
        }
        return true;
      });
    }

    const hasSameMatch = getComboOrSystemBets(latestOdds()).some((odd, idx, arr) => {
      return idx > 0 && odd.matchId === arr[idx - 1].matchId;
    });

    // 单注投注：检查所有投注项都有金额
    return store.comboAmount > 0 && latestOdds().length > 1 && !hasSameMatch;
  });

  // system 模式不能超过十个胆
  const isOverSystemBetCount = createMemo(() => {
    if (store.betType === BetSlipType.System && getComboOrSystemBets(latestOdds()).length > 10) return true;

    return false;
  });

  const betAmount = createMemo(() => currency.convertCurrency(totalBet(), latestCurrency(), latestCurrency()));

  const enoughBalance = createMemo(() => new Decimal(addVipBalance()).greaterThanOrEqualTo(betAmount()));
  const isCostCredit = createMemo(() => {
    return betAmount().gt(0) && isBetCreditOn() && currencyAvailableCreditBalance() > 0 && isCash();
  });
  const costCashAmount = createMemo(() => {
    const cashAmount = new Decimal(currencyCashBalanceDecimal());
    return cashAmount.isNegative() ? new Decimal(0) : cashAmount;
  });
  const costCreditAmount = createMemo(() => {
    if (!isCostCredit()) return new Decimal(0);
    const isGreater = new Decimal(betAmount()).greaterThanOrEqualTo(new Decimal(currencyAvailableCreditBalance()));
    return isGreater ? new Decimal(currencyAvailableCreditBalance()) : new Decimal(betAmount());
  });
  // place bet disables reason
  const isBetDisabled = createMemo(() => {
    // console.log(
    //   '是否可投注🤔',
    //   isOverSystemBetCount()
    // );
    return hasClosedOdds() || !canBet() || !enoughBalance() || isOverSystemBetCount();
  });

  const betAction = () => {
    const navigate = useNavigate();
    return (
      <>
        <Overlay
          ref={overlayRef}
          type="general"
          status={status()}
          countdownSeconds={countdownSeconds()}
          height={'19.5rem'}
          localErrCode={localErrCode()}
          message={message()}
          isOpen={showOverlay()}
          class={cx('fixed')}
          onClose={() => setShowOverlay(false)}
          onConfirm={async () => {
            setShowOverlay(false);
            // 接受最新赔率 并且重新下单
            if (
              status() === BetslipStatus.SINGLE_ODDS_ALL_FAILED ||
              status() === BetslipStatus.SINGLE_ODDS_PARTIALLY_PLACED
            ) {
              dispatch({
                type: 'SET_ODDS_SETTINGS',
                settings: OddsSettings.ACCEPT_ANY_ODDS_CHANGE,
              });
              await createOrder();
            }
          }}
        />
        <Show
          when={account.isLogin}
          fallback={
            <Button
              type="brand"
              onClick={() => {
                navigate('/login');
              }}
              class="w-full h-11 font-semibold mt-2"
            >
              {t('Log in')}
            </Button>
          }
        >
          <Button
            onClick={async () => {
              if (!enoughBalance()) return;
              if (
                CheckCreditBetLimit({
                  costCreditAmount: costCreditAmount(),
                  maxCreditBet: maxCreditBet(),
                  latestCurrency: latestCurrency(),
                  isBonus: isBonus(),
                })
              ) {
                return;
              }
              if (!canDeposit()) {
                toast.warning(t('Action unavailable due to self-exclusion.'));
                return;
              }
              if (!canPlaceBet()) {
                toast.warning(t('You have reached your betting limit'));
                return;
              }
              await createOrder();
              await getOpenBetCount();
            }}
            disabled={isBetDisabled()}
            type="brand"
            class="w-full h-11 caption-m-semibold text-neutral-alw-black mt-2 rounded-lg"
            style={{
              display: enoughBalance() ? 'block' : 'none',
            }}
          >
            <Show when={enoughBalance()} fallback={t('Deposit to Place Bet')}>
              {isCostCredit() ? t('Place Bet (Credits Included)') : t('Place Bet')}
            </Show>
          </Button>
        </Show>

        {/* 仅在余额不足并且有下注项时，显示提示和 Deposit 链接（不替换主按钮） */}
        <Show when={account.isLogin && !enoughBalance() && latestOdds().length > 0}>
          <Show
            when={isCash()}
            fallback={
              <Button type="brand" disabled class="w-full my-2 h-11 font-semibold">
                {t('Place Bet')}
              </Button>
            }
          >
            <A
              href={
                isNegative() ? getNegativeDepositUrl() : `/deposit?tab=${isFiat(latestCurrency()) ? 'Fiat' : 'crypto'}`
              }
            >
              <Button type="brand" class="w-full my-2 h-11 font-semibold">
                {t('Deposit to Place Bet')}
              </Button>
            </A>
          </Show>
          <div class="text-xs flex gap-1 items-center bg-alpha-function-orange-a10 text-function-alert font-semibold h-7 px-2 py-[.375rem] rounded-full">
            <IconCheckWarning class="size-4" />
            {t('Your bet amount cannot exceed the balance')}
          </div>
        </Show>
      </>
    );
  };

  // system label
  const ComboTypeLabel = (props: { fullName: string }) => {
    return (
      <div
        class={cx(
          'flex justify-between items-center h-[42px] w-full px-3 py-2 cursor-pointer mb-[.625rem] rounded-md bg-layer3 text-neutral-main1',
          isSelectOpen() ? 'border border-brand' : ''
        )}
      >
        <span class="caption-m-semibold">{props.fullName}</span>
        <IconArrow06 class={cx('size-6', isSelectOpen() ? 'rotate-180' : '')} />
      </div>
    );
  };

  const [comboTypeSelected, setComboTypeSelected] = createSignal<string>(store.comboType);

  // system type option label
  const ComboTypeOptionLabel = (props: { value: string; betsCount: number; desc: string }) => (
    <div class="flex items-center gap-1">
      <Radio value={props.value} bind={[comboTypeSelected, setComboTypeSelected]}>
        <span class="ml-2 text-neutral-main2 caption-m-medium">
          {props.value} ({props.betsCount} {t('Bets')})
        </span>
      </Radio>

      <Tooltip content={props.desc}>
        <span class="center w-4 h-4" onClick={(e) => e.stopPropagation()}>
          <IconCheckQuestion class="size-base text-neutral-main2 cursor-pointer opacity-70 hover:opacity-100" />
        </span>
      </Tooltip>
    </div>
  );

  const [systemTypeList, setSystemTypeList] = createSignal<
    { value: string; betsCount: number; composition: string; eventsCount: number }[]
  >([]);
  const [hasFetchedSystemTypes, setHasFetchedSystemTypes] = createSignal(false);
  const [isTotalDetailOpen, setIsTotalDetailOpen] = createSignal(false);
  const [isSystemStakeTipVisible, setIsSystemStakeTipVisible] = createSignal(false);
  function idleFetch(fn: () => void) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(fn, { timeout: 500 });
    } else {
      setTimeout(fn, 16);
    }
  }

  const systemTypeOptions = createMemo(() => {
    const list = systemTypeList();
    const count = getComboOrSystemBets(latestOdds()).length;

    if (!list.length || count === 0) return [];

    return list
      .filter((o) => o.eventsCount === count)
      .map((item) => ({
        fullName: `${item.value} (${item.betsCount} ${t('Bets')})`,
        betCount: item.betsCount,
        value: item.value,
        label: <ComboTypeOptionLabel value={item.value} betsCount={item.betsCount} desc={item.composition} />,
      }));
  });

  createEffect(() => {
    if (store.betType !== BetSlipType.System) return;

    // 👇 只请求一次
    if (hasFetchedSystemTypes()) return;

    setHasFetchedSystemTypes(true);

    idleFetch(async () => {
      const list = await get.getTicketsComboTypes();
      setSystemTypeList(list);
    });
  });

  const selectedOption = createMemo(() => systemTypeOptions().find((o) => o.value === getComboType()));

  // 当store.odd变化时，option的默认value值也应该变化
  createEffect(() => {
    if (store.betType !== BetSlipType.System) return;

    const options = systemTypeOptions();
    if (!options.length) return;

    const current = getComboType();
    const defaultValue = options[0].value;

    const isValid = options.some((o) => o.value === current);
    const effectiveValue = isValid ? current : defaultValue;

    if (current !== effectiveValue) {
      dispatch({
        type: 'SET_BET_SYSTEM_COMBO_TYPE',
        comboType: effectiveValue,
      });
    }
    setComboTypeSelected(effectiveValue);
  });

  // selections之间是否可以组合
  const IsCanCombinable = createMemo(() => {
    if (store.betType === BetSlipType.Single) {
      return hasClosedOdds();
    } else {
      return getComboOrSystemBets(store.odds).some((odd, idx) => odd.matchId === store.odds[idx - 1]?.matchId);
    }
  });

  const getInputVal = createMemo(() => (store.comboAmount === 0 ? undefined : store.comboAmount));

  return (
    <div
      class={cx('px-3 pb-4 pt-[.75rem] w-full z-10', store.odds.length > 0 ? 'bg-layer5-card' : 'bg-betslip-body-bg')}
    >
      <Show when={store.odds.length > 0}>
        {/* 串关金额输入框 */}
        <Show when={store.betType === BetSlipType.Combo || store.betType === BetSlipType.System}>
          <Transition.Slide y>
            <Transition.Fade>
              <div class="relative">
                <Show when={isOverSystemBetCount()}>
                  <div class="mb-2 flex gap-1 items-center bg-alpha-function-orange-a10 text-function-alert text-xs font-semibold px-2 py-1">
                    <IconCheckQuestion class="size-4" />
                    {t('Selections cannot over 10')}
                  </div>
                </Show>

                <Show when={store.betType === BetSlipType.System}>
                  <span class="flex items-center justify-start text-neutral-main3 caption-m-medium mb-[.38rem]">
                    {t('System')}
                    <Tooltip content={t('Choose one of the system types')} position="top">
                      <IconCheckQuestion class="size-4 ml-[.12rem] cursor-pointer" />
                    </Tooltip>
                  </span>

                  <Select
                    onVisibleChange={(visible) => setIsSelectOpen(visible)}
                    bind={[
                      selectedOption,
                      (value) => {
                        dispatch({
                          type: 'SET_BET_SYSTEM_COMBO_TYPE',
                          comboType: value.value,
                        });
                      },
                    ]}
                    tabIndex={0}
                    type="secondary"
                    placement="top"
                    options={systemTypeOptions()}
                    label={<ComboTypeLabel fullName={selectedOption()?.fullName ?? ''} />}
                    layerClass="bg-layer4 max-h-[140px]"
                    footer={
                      <div class="px-4 py-3 bg-layer5_5-card h-[30px] -mb-[5px] z-10">
                        <span class="block -mt-1 text-center text-neutral-main3 caption-s-medium">
                          {t('The type depends on the number of selections')}
                        </span>
                      </div>
                    }
                  />
                </Show>

                <Show when={store.betType === BetSlipType.System}>
                  <Input.Number
                    class={cx(
                      'w-full h-[42px] mb-2 border-main3 bg-layer3 caption-m-semibold transition-all hover:border-main2 focus-within:border-primary-main [&_input::placeholder]:text-neutral-main5 [&_input:focus::placeholder]:text-neutral-main7',
                      isSystemStakeTipVisible() ? '!border-red hover:!border-red focus-within:!border-red' : ''
                    )}
                    value={getInputVal()}
                    onInput={(v) =>
                      dispatch({
                        type: 'SET_COMBO_AMOUNT',
                        amount: Number(v),
                      })
                    }
                    suffix={
                      <div class="mr-2">
                        <span class="center text-neutral-main1 -mr-1">
                          <img class="size-[18px]" src={getCoinIcon(latestCurrency())} alt={latestCurrency()} />
                        </span>
                      </div>
                    }
                    // precision={currencyConfig[latestCurrency()]?.showUnit ?? 2}
                    preserveTrailingZeros={Number(currencyConfig[latestCurrency()]?.currencyType) === 0}
                    placeholder={t('Stake Amount')}
                  />

                  {/* 盘口投注金额限制提示 */}
                  <BetStakeInputTip
                    amount={store.comboAmount}
                    isShowOddsLimit={isShowOddsLimit}
                    onVisibleChange={setIsSystemStakeTipVisible}
                  />
                </Show>
              </div>
            </Transition.Fade>
          </Transition.Slide>
        </Show>

        {/* 快速投注按钮区域 */}
        <Show when={account.isLogin}>
          <div class="flex flex-col w-full">
            <ActionLocked lockedKey={LockedKey.bet_Fast_bet_amount}>
              <div class="flex gap-2 mb-1">
                <For each={store.fastBetOptions}>
                  {(amount) => {
                    // 检查当前快速投注金额是否匹配
                    const isAmountMatch = () => {
                      if (store.betType === BetSlipType.Combo || store.betType === BetSlipType.System) {
                        return store.comboAmount === amount;
                      }
                      return latestOdds().length === 0 ? false : latestOdds().every((odd) => odd.amount === amount);
                    };

                    return (
                      <Button
                        type="second"
                        class={cx(
                          'flex-1 h-9 rounded-lg border border-main3 bg-button-general-secondary caption-m-semibold text-neutral-main1',
                          isAmountMatch() ? '!border-brand !text-brand-default' : ''
                        )}
                        disabled={latestOdds().length === 0}
                        onClick={() =>
                          dispatch({
                            type: 'FAST_BET',
                            amount,
                          })
                        }
                      >
                        {amount}
                      </Button>
                    );
                  }}
                </For>
              </div>
            </ActionLocked>
            <div>
              <VipCreditNotice class="mb-4" isBetSlip bind={[isBetCreditOn, setIsBetCreditOn]} />
            </div>
            {/* 总投注额显示 */}
            <StatBlock>
              <span class="text-neutral-main2 caption-l-medium">{t('Total Stake')}</span>
              <div class="flex flex-col text-right caption-l-medium text-neutral-main1">
                <span class="center text-neutral-main1">
                  <Show when={!isCash()}>
                    <span class="center bg-alpha-bw-autowhite-a10 mr-[6px] text-neutral-main1 text-[10px] rounded-2xl h-5 px-[.38rem]">
                      {t('Bonus')}
                    </span>
                  </Show>
                  <span class="min-w-0 max-w-[150px] truncate font-bold">
                    {currencyConvertHint(totalBet(), latestCurrency(), latestCurrency(), 'transaction')}
                  </span>
                  <img class="size-[18px] ml-1" src={getCoinIcon(latestCurrency())} alt={latestCurrency()} />
                  {/* <Show when={isCostCredit()}>
                    <IconArrow06
                      class={cx(
                        'size-4 cursor-pointer text-neutral-main3 transition-transform',
                        isTotalDetailOpen() ? 'rotate-180' : ''
                      )}
                      onClick={() => setIsTotalDetailOpen(!isTotalDetailOpen())}
                    />
                  </Show> */}
                </span>
                <Show when={!isFiat(latestCurrency()) && walletSettings.showCurrency !== latestCurrency()}>
                  <span
                    class={cx('text-xs opacity-60 min-w-0 max-w-[150px] truncate', isCostCredit() ? 'mr-[1.1rem]' : '')}
                  >
                    ≈ {currencyConvertHint(totalBet(), latestCurrency(), walletSettings.showCurrency, 'display')}
                  </span>
                </Show>
              </div>
            </StatBlock>
            {/* <Toggle when={isTotalDetailOpen() && isCostCredit()}>
              <div class="flex items-center justify-between">
                <div class="text-neutral-main5">{t('Cash')}</div>
                <div class="flex items-center">
                  <img class="size-3 mr-1" src={getCoinIcon(latestCurrency())} alt={latestCurrency()} />
                  {currencyConvertHint(costCashAmount(), latestCurrency(), latestCurrency(), 'transaction')}
                </div>
              </div>
              <div class="flex items-center justify-between">
                <div class="text-neutral-main5">{t('VIP Credits')}</div>
                <div class="flex items-center">
                  <img class="size-3 mr-1" src={getCoinIcon(latestCurrency())} alt={latestCurrency()} />
                  {currencyConvertHint(costCreditAmount(), latestCurrency(), latestCurrency(), 'transaction')}
                </div>
              </div>
            </Toggle> */}
            {/* 潜在收益显示 */}
            <StatBlock>
              <span class="text-neutral-main2 capitalize flex items-center gap-[2px]">
                <span class="text-neutral-main2 caption-l-medium">{t('potential win')}</span>
                <Show when={!isCash()}>
                  <Tooltip content={t('Bonus stakes not included. Only profit paid as cash.')} position="bottom">
                    <IconCheckQuestion class="size-4 ml-[.12rem] cursor-pointer" />
                  </Tooltip>
                </Show>
              </span>
              <div class="flex flex-col text-sm text-right caption-l-medium text-neutral-main1">
                <span class="center text-neutral-main1">
                  <Show
                    when={!IsCanCombinable() && !hasClosedOdds()}
                    fallback={<span class="text-neutral-main2">-</span>}
                  >
                    <span class="min-w-0 max-w-[150px] truncate font-bold">
                      {currencyConvertHint(finalTotalWin(), latestCurrency(), latestCurrency(), 'transaction')}
                    </span>
                  </Show>
                  <img class="size-[18px] ml-1" src={getCoinIcon(latestCurrency())} alt={latestCurrency()} />
                </span>
                <Show when={!isFiat(latestCurrency()) && walletSettings.showCurrency !== latestCurrency()}>
                  <span class="text-xs min-w-0 max-w-[150px] truncate">
                    ≈ {currencyConvertHint(finalTotalWin(), latestCurrency(), walletSettings.showCurrency, 'display')}
                  </span>
                </Show>
              </div>
            </StatBlock>
          </div>
        </Show>
      </Show>

      {/* 投注按钮 */}
      {betAction()}
    </div>
  );
};
